import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface DatePickerProps {
  id?: string;
  value: string | null;
  onChange: (value: string | null) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * Thin wrapper over the native date input.
 *
 * The native control already produces/consumes ISO 8601 YYYY-MM-DD strings,
 * renders a calendar picker in modern browsers, and is keyboard-accessible —
 * so we avoid the dependency weight of react-day-picker for the two sites
 * that currently need a date field.
 *
 * Emits `null` on clear so callers can use `null` to mean "no due date".
 */
export function DatePicker({ id, value, onChange, disabled, className }: DatePickerProps) {
  return (
    <div className={cn("flex items-center gap-2", className)}>
      <Input
        id={id}
        type="date"
        value={value ?? ""}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)}
        className="max-w-[12rem]"
      />
      {value && !disabled && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onChange(null)}
          aria-label="Clear date"
        >
          Clear
        </Button>
      )}
    </div>
  );
}
